import { useEffect, useMemo, useState } from "react"
import classNames from "classnames"
import ColorPicker from "./ColorPicker"
import { AlertColorForm } from "."
import { Icon, IconColor } from "@/components/shared/Icon"
import Tooltip from "@/components/shared/Tooltip"
import { AlertVariant, TypefaceVariant } from "@/types/alert"

const ColorGroup = ({
   label,
   valueKey,
   isEditing,
   form,
   iconColor,
   setFormState,
}: {
   label: string
   valueKey: keyof AlertColorForm
   isEditing: boolean
   form: AlertColorForm
   iconColor: IconColor
   setFormState: (form: AlertColorForm) => void
}) => {
   const [cardOptionsVisible, setCardOptionsVisible] = useState(false)
   const [typefaceOptionsVisible, setTypefaceOptionsVisible] = useState(false)
   const value = form[valueKey]

   const colorCardVariant = useMemo(() => {
      return valueKey === "colorCard"
         ? `--${AlertVariant[value as keyof typeof AlertVariant]}`
         : `--${TypefaceVariant[value as keyof typeof TypefaceVariant]}`
   }, [value, valueKey])

   const handleColorSelect = (option: AlertVariant | TypefaceVariant) => {
      setFormState({
         ...form,
         [valueKey]: option,
      })
      if (valueKey === "colorCard") {
         setCardOptionsVisible(false)
      } else {
         setTypefaceOptionsVisible(false)
      }
   }

   useEffect(() => {
      if (!isEditing) {
         if (cardOptionsVisible) {
            setCardOptionsVisible(false)
         }
         if (typefaceOptionsVisible) {
            setTypefaceOptionsVisible(false)
         }
      }
   }, [cardOptionsVisible, isEditing, typefaceOptionsVisible])

   return (
      <div className={"alert-color-setting__color-group"}>
         <div className="alert-color-setting__subtitle">{label}</div>
         <Tooltip
            key="color-picker-tooltip"
            open={cardOptionsVisible || typefaceOptionsVisible}
            content={
               <ColorPicker
                  valueKey={valueKey}
                  options={
                     cardOptionsVisible
                        ? (Object.values(AlertVariant) as AlertVariant[])
                        : (Object.values(TypefaceVariant) as TypefaceVariant[])
                  }
                  onColorSelect={(option) => handleColorSelect(option)}
               />
            }
         >
            <div
               className={classNames(
                  "alert-color-setting__color-card",
                  `alert-color-setting__color-card${colorCardVariant}`,
                  {
                     "alert-color-setting__color-card--editable": isEditing,
                  }
               )}
               {...(isEditing && {
                  onClick: () =>
                     valueKey === "colorCard"
                        ? setCardOptionsVisible(!cardOptionsVisible)
                        : setTypefaceOptionsVisible(!typefaceOptionsVisible),
               })}
            >
               {isEditing && (
                  <Icon
                     name="brush"
                     color={iconColor}
                  />
               )}
            </div>
         </Tooltip>
      </div>
   )
}

export default ColorGroup
